import type { UseMutationResult } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import useUser from 'shared/hooks/use-user'
import type { UseMutationClearOptions } from 'shared/types/tanstack-utility-types'
import { useLogoUrl } from '../hooks/use-logo-url'

export interface WorkspaceSwitchPayload {
  currentWorkspaceMembership: number | null
}

type UseWorkspaceSelectOptions = Omit<
  UseMutationClearOptions<void, Error, WorkspaceSwitchPayload>,
  'onSuccess' | 'retry'
>

export const useWorkspaceSelect = (
  options: UseWorkspaceSelectOptions = {},
): UseMutationResult<void, Error, WorkspaceSwitchPayload> => {
  const { mutate } = useUser()
  const router = useRouter()
  const redirectUrl = useLogoUrl()
  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.post,
    void,
    WorkspaceSwitchPayload
  >({
    method: RequestMethodsEnum.post,
    accessDeniedResponseHandler: error => {
      throw error
    },
  })

  return useMutation({
    mutationFn: (data: WorkspaceSwitchPayload) => fetcher('/api/settings/workspace', data),
    retry: false,
    onSuccess: async () => {
      try {
        await mutate()

        if (redirectUrl && router.pathname !== redirectUrl) {
          await router.push(redirectUrl)
        }
      } finally {
        router.reload()
      }
    },
    ...options,
  })
}
